// Which document a party of a transaction can download (downloadable
// receipts spec §4). CommonJS: shared by the receipt endpoint and the
// transaction page.
//
// No object spread/rest in this file: Babel turns it into an ES-module helper
// import, which makes webpack treat this CommonJS file as an ES module and
// `module.exports = …` then throws when the client bundle loads.

const RECEIPT_KIND = {
  CUSTOMER_RECEIPT: 'customer-receipt',
  PROVIDER_RECEIPT: 'provider-receipt',
  PROVIDER_BILLING_BASIS: 'provider-billing-basis',
};

// The same strings as the VAT engine's PATH (src/util/vat/transaction.js).
const RECEIPT_PATH = { STRIPE: 'stripe', LIGHT_MAIN: 'light-main', INVOICE: 'invoice' };

const PROCESSES = ['flex-default-process', 'flex-light-process'];
const PAID_TRANSITIONS = ['transition/confirm-payment', 'transition/confirm-project'];
const DELIVERED_TRANSITIONS = [
  'transition/release-funds',
  'transition/job-done-release-funds',
  'transition/accept-job-invoice',
  'transition/job-done-accept-invoice',
];
const CANCEL_TRANSITIONS = ['transition/cancel', 'transition/cancel-invoice'];
const INVOICE_ACCEPT_TRANSITION = 'transition/accept-custom-offer-invoice';

const transitionNames = tx =>
  ((tx.attributes && tx.attributes.transitions) || []).map(t => t.transition);

// The customer service fee layout (spec 2026-10-05): the fee is the
// customer's line, nothing is deducted from the provider.
const hasCustomerFeeLayout = lineItems => {
  const codes = (lineItems || []).map(li => li.code);
  return (
    codes.indexOf('line-item/customer-commission') !== -1 &&
    codes.indexOf('line-item/provider-commission') === -1
  );
};

// From facts no party can rewrite: the process and the transition history.
const receiptPathFor = tx => {
  if (transitionNames(tx).indexOf(INVOICE_ACCEPT_TRANSITION) !== -1) return RECEIPT_PATH.INVOICE;
  return tx.attributes.processName === 'flex-light-process'
    ? RECEIPT_PATH.LIGHT_MAIN
    : RECEIPT_PATH.STRIPE;
};

// providerAccountRole: the provider's profile publicData.account_role.
const receiptKindFor = (tx, role, providerAccountRole) => {
  const attrs = (tx && tx.attributes) || {};
  if (PROCESSES.indexOf(attrs.processName) === -1) return null;
  // The server-only copy (spec §5.5); protectedData.vat is party-writable.
  const vat = attrs.metadata && attrs.metadata.vat;
  if (!vat || vat.v !== 2) return null;
  if (!hasCustomerFeeLayout(attrs.lineItems)) return null;
  if (CANCEL_TRANSITIONS.indexOf(attrs.lastTransition) !== -1) return null;

  const names = transitionNames(tx);
  const has = list => names.some(name => list.indexOf(name) !== -1);
  const path = receiptPathFor(tx);
  if (role === 'customer') {
    return path !== RECEIPT_PATH.INVOICE && has(PAID_TRANSITIONS)
      ? RECEIPT_KIND.CUSTOMER_RECEIPT
      : null;
  }
  if (role === 'provider') {
    if (!has(DELIVERED_TRANSITIONS)) return null;
    if (path === RECEIPT_PATH.STRIPE) return RECEIPT_KIND.PROVIDER_RECEIPT;
    // Freedomly pays a freelancer directly and sends the billing basis by hand.
    return providerAccountRole === 'freelancer' ? null : RECEIPT_KIND.PROVIDER_BILLING_BASIS;
  }
  return null;
};

// Receipts speak Finnish unless English is asked for.
const receiptLocale = locale =>
  String(locale || '')
    .toLowerCase()
    .slice(0, 2) === 'en'
    ? 'en'
    : 'fi';

const FILE_STEMS = {
  'customer-receipt': { fi: 'freedomly-kuitti', en: 'freedomly-receipt' },
  'provider-receipt': { fi: 'freedomly-kuitti', en: 'freedomly-receipt' },
  'provider-billing-basis': { fi: 'freedomly-laskutusperuste', en: 'freedomly-billing-basis' },
};

const receiptFilename = (kind, transactionId, locale) =>
  `${FILE_STEMS[kind][receiptLocale(locale)]}-${String(transactionId).slice(0, 8)}.pdf`;

module.exports = {
  RECEIPT_KIND,
  RECEIPT_PATH,
  PAID_TRANSITIONS,
  DELIVERED_TRANSITIONS,
  receiptKindFor,
  receiptPathFor,
  receiptLocale,
  receiptFilename,
};
